<script lang="ts">
  import { type DrawerSettings, getDrawerStore, getToastStore } from '@skeletonlabs/skeleton'
  import { onMount } from 'svelte'

  import ThemeToggle from './ThemeToggle.svelte'
  import GitHub from '@components/GitHub.svelte'
  import Hirundo from '@components/Hirundo.svelte'
  import Link from '@components/Link.svelte'
  import SubMenu from '@components/SubMenu.svelte'
  import { loggedInStore } from '@stores/logged-in-store'
  import { airGappedUserPasswordLocalStorage, forceLogout, getAuthProvider } from '@utils/auth'
  import { type Config, getConfig } from '@utils/config.svelte'
  import {
    getLabelStudioConfig,
    type LabelStudioConfig,
    resetLabelStudioSessionState,
  } from '@utils/label-studio'
  import { buildManageItems, buildProductSubMenus, type MobileDrawerMeta } from '@utils/navigation'
  import { Product } from '@utils/product'

  const drawerStore = getDrawerStore()
  let config: Config | undefined = $state()

  const logout = async () => {
    if (localStorage.getItem(airGappedUserPasswordLocalStorage)) {
      localStorage.removeItem(airGappedUserPasswordLocalStorage)
    }
    const resolvedConfig = config ?? (await getConfig(toastStore))
    config = resolvedConfig
    await forceLogout(true, getAuthProvider(resolvedConfig))
  }

  const toastStore = getToastStore()

  const llmOnlyProducts = [
    Product.UNLEARNING_LLM_BEHAVIOR,
    Product.UNLEARNING_LLM_DATA,
    Product.LLM_BEHAVIOR_EVAL,
  ]
  let activeProducts = $state<Product[]>([])
  const productSubMenus = $derived(buildProductSubMenus(activeProducts))
  let labelStudioEnabled = $state(false)
  const hasOnlyLlmProducts = $derived(
    activeProducts.length > 0 &&
      activeProducts.every((product) => llmOnlyProducts.includes(product)),
  )
  const showStorageLink = $derived(!hasOnlyLlmProducts)

  const manageItems = $derived(buildManageItems(labelStudioEnabled))

  onMount(async () => {
    config = await getConfig(toastStore)
    activeProducts = config?.activeProducts?.map((product) => product as Product) || []
  })

  const loadLabelStudioConfig = async () => {
    const config: LabelStudioConfig = await getLabelStudioConfig(toastStore)
    labelStudioEnabled = config.enabled
  }

  $effect(() => {
    if ($loggedInStore.loggedIn) {
      void loadLabelStudioConfig()
    } else {
      labelStudioEnabled = false
      resetLabelStudioSessionState()
    }
  })

  const toggleMobileNavigation = () => {
    if ($drawerStore.id === 'mobile-nav' && $drawerStore.open) {
      drawerStore.close()
      return
    }
    drawerStore.open(buildDrawerSettings())
  }

  const shouldUpdateDrawerMeta = (meta?: MobileDrawerMeta) => {
    if (!meta) {
      return true
    }
    return (
      meta.manageItems !== manageItems ||
      meta.productSubMenus !== productSubMenus ||
      meta.showStorageLink !== showStorageLink ||
      meta.logout !== logout
    )
  }

  const buildDrawerSettings = (): DrawerSettings => ({
    id: 'mobile-nav',
    position: 'left',
    bgDrawer: 'bg-surface-50 text-surface-900 dark:bg-surface-900 dark:text-surface-50 z-[1400]',
    bgBackdrop: 'bg-surface-900/40 z-[1390]',
    width: 'w-screen',
    padding: 'p-0',
    rounded: 'rounded-none',
    meta: {
      manageItems,
      productSubMenus,
      showStorageLink,
      logout,
    },
  })

  $effect(() => {
    if ($drawerStore.id !== 'mobile-nav' || !$drawerStore.open) {
      return
    }
    const currentMeta = $drawerStore.meta as MobileDrawerMeta | undefined
    if (!shouldUpdateDrawerMeta(currentMeta)) {
      return
    }
    drawerStore.open(buildDrawerSettings())
  })

  onMount(() => {
    if (typeof window === 'undefined') {
      return
    }

    const mediaQueryList = window.matchMedia('(min-width: 1024px)')
    const closeMobileNavigation = () => {
      if ($drawerStore.id === 'mobile-nav' && $drawerStore.open) {
        drawerStore.close()
      }
    }
    const handleMediaChange = (event: MediaQueryListEvent) => {
      if (event.matches) {
        closeMobileNavigation()
      }
    }

    if (mediaQueryList.matches) {
      closeMobileNavigation()
    }

    if ('addEventListener' in mediaQueryList) {
      mediaQueryList.addEventListener('change', handleMediaChange)
      return () => mediaQueryList.removeEventListener('change', handleMediaChange)
    }
  })
</script>

<header class="sticky left-0 z-50 flex items-center justify-between gap-4">
  <div
    class="ml-4 flex h-12 w-32 items-center justify-center text-primary-500 dark:text-surface-50"
  >
    <a href="https://hirundo.io" class="flex h-full w-full items-center justify-center">
      <Hirundo currentColor="currentColor" />
    </a>
  </div>

  {#if $loggedInStore.loggedIn}
    <nav class="hidden flex-1 items-center justify-center lg:flex">
      <ul
        class="relative flex h-12 items-center justify-center gap-2 rounded-full border border-primary-200 bg-primary-100 px-4 text-surface-900 dark:border-surface-600 dark:bg-surface-700 dark:text-surface-50"
      >
        <SubMenu title="Manage" path="/manage" items={manageItems} />
        {#if showStorageLink}
          <Link path="/storage-configs">Storage</Link>
        {/if}
        {#each productSubMenus as subMenu (subMenu.title)}
          {#if subMenu.items}
            <SubMenu title={subMenu.title} path={subMenu.path} items={subMenu.items} />
          {:else}
            <Link path={subMenu.path}>
              {subMenu.title}
            </Link>
          {/if}
        {/each}
        <Link onclick={logout}>Logout</Link>
      </ul>
    </nav>
  {/if}

  <div class="mr-4 flex items-center gap-3">
    <div class="flex">
      <ThemeToggle />
    </div>
    {#if $loggedInStore.loggedIn}
      <button
        class="variant-ghost btn-icon lg:hidden"
        type="button"
        aria-label="Open navigation menu"
        onclick={toggleMobileNavigation}
      >
        <svg viewBox="0 0 24 24" aria-hidden="true" class="h-5 w-5">
          <path
            d="M4 7h16M4 12h16M4 17h16"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
          />
        </svg>
      </button>
    {/if}

    <div class="hidden h-10 w-10 items-center justify-center lg:flex">
      <a
        href="https://github.com/Hirundo-io/"
        class="flex h-full w-full items-center justify-center text-surface-900 dark:text-white"
      >
        <GitHub />
      </a>
    </div>
  </div>
</header>
